/**
 * Smart Match State Management Hook
 * Manages the state flow through all 3 steps of Smart Match functionality
 */

import { useState, useCallback } from "react";
import { ExcelTableData } from "../../shared/excelDataExtractor";
import { SMART_MATCH_DEFAULTS } from "../../shared/constants";

export interface ColumnMapping {
  id: string;
  left: string;
  right: string;
}

export interface SmartMatchState {
  // Step 1: Select Data Source
  leftDataSource: string | null;
  rightDataSource: string | null;
  leftTableData: ExcelTableData | null;
  rightTableData: ExcelTableData | null;
  
  // Matching Intent (user-provided or AI-generated)
  matchingIntent: string | null;
  
  // Grounding Table Information (LLM-driven)
  groundedTable: string | null; // Which table is grounded (left or right table name)
  groundingReasoning: string | null; // LLM reasoning for grounding decision
  isGroundingLoading: boolean;
  
  // Step 2: Map Columns
  columnMappings: ColumnMapping[];
  mappingGroups: any[]; // Store the complete mapping groups structure
  selectedOutputColumns: Set<string>;

 // NEW: Formula Caching
 cachedMatchingFormulas: string | null; // Encrypted formula string from backend

  
  // Step 3: Review & Configure
  threshold: number;
  maxMatches: number | null;
  handleMissingValues: string;
  
  // UI State
  currentStep: number;
  isLoading: boolean;
  error: string | null;
}

const INITIAL_STATE: SmartMatchState = {
  // Step 1
  leftDataSource: null,
  rightDataSource: null,
  leftTableData: null,
  rightTableData: null,
  
  // Matching Intent
  matchingIntent: null,
  
  // Grounding Table Information
  groundedTable: null,
  groundingReasoning: null,
  isGroundingLoading: false,

  cachedMatchingFormulas: null,
  
  // Step 2
  columnMappings: [],
  mappingGroups: [],
  selectedOutputColumns: new Set(),
  
  // Step 3
  threshold: SMART_MATCH_DEFAULTS.DEFAULT_THRESHOLD,
  maxMatches: null,
  handleMissingValues: "Flag for review",
  
  // UI
  currentStep: 1,
  isLoading: false,
  error: null
};

export function useSmartMatch() {
  const [state, setState] = useState<SmartMatchState>(INITIAL_STATE);
  const [prevState, setPrevState] = useState<SmartMatchState>(INITIAL_STATE)

  // Step 1: Set data sources
  const setLeftDataSource = useCallback((source: string, tableData: ExcelTableData) => {
    setState(prev => ({
      ...prev,
      leftDataSource: source,
      leftTableData: tableData,
      error: null
    }));
  }, []);

  const setRightDataSource = useCallback((source: string, tableData: ExcelTableData) => {
    setState(prev => ({
      ...prev,
      rightDataSource: source,
      rightTableData: tableData,
      error: null
    }));
  }, []);


  // Step 2: Column mapping management
  const addColumnMapping = useCallback((left: string, right: string) => {
    setState(prev => {
      // Check if this exact mapping already exists
      const exists = prev.columnMappings.some(
        m => m.left === left && m.right === right
      );
      
      if (exists) {
        return prev;
      }

      const newMapping: ColumnMapping = {
        id: `${Date.now()}_${Math.random()}`,
        left,
        right
      };

      return {
        ...prev,
        columnMappings: [...prev.columnMappings, newMapping]
      };
    });
  }, []);

  const removeColumnMapping = useCallback((mappingId: string) => {
    setState(prev => ({
      ...prev,
      columnMappings: prev.columnMappings.filter(m => m.id !== mappingId)
    }));
  }, []);

  const clearColumnMappings = useCallback(() => {
    setState(prev => ({
      ...prev,
      columnMappings: [],
      mappingGroups: []
    }));
  }, []);

  // Swap column mappings (left <-> right) in a single state update
  const swapColumnMappingsInState = useCallback(() => {
    setState(prev => {
      // Swap column mappings
      const swappedMappings = prev.columnMappings.map(mapping => ({
        ...mapping,
        left: mapping.right,
        right: mapping.left
      }));

      // Swap mapping groups
      const swappedGroups = prev.mappingGroups.map(group => ({
        ...group,
        mappings: group.mappings.map(mapping => ({
          ...mapping,
          leftColumns: mapping.rightColumns,
          rightColumns: mapping.leftColumns
        }))
      }));

      // Swap output columns
      const swappedOutputColumns = new Set<string>();
      prev.selectedOutputColumns.forEach(columnId => {
        if (columnId.startsWith('left_')) {
          swappedOutputColumns.add('right_' + columnId.substring(5));
        } else if (columnId.startsWith('right_')) {
          swappedOutputColumns.add('left_' + columnId.substring(6));
        } else {
          swappedOutputColumns.add(columnId);
        }
      });

      return {
        ...prev,
        columnMappings: swappedMappings,
        mappingGroups: swappedGroups,
        selectedOutputColumns: swappedOutputColumns
      };
    });
  }, []);

  // Mapping Groups management
  const setMappingGroups = useCallback((groups: any[]) => {
    setState(prev => ({
      ...prev,
      mappingGroups: groups
    }));
  }, []);

  // Output columns selection
  const toggleOutputColumn = useCallback((columnName: string) => {
    setState(prev => {
      const newSet = new Set(prev.selectedOutputColumns);
      if (newSet.has(columnName)) {
        newSet.delete(columnName);
      } else {
        newSet.add(columnName);
      }
      return {
        ...prev,
        selectedOutputColumns: newSet
      };
    });
  }, []);

  const setAllOutputColumns = useCallback((columns: string[]) => {
    setState(prev => ({
      ...prev,
      selectedOutputColumns: new Set(columns)
    }));
  }, []);


    //Matching formulas

    const setCachedMatchingFormulas = useCallback((formulas: string | null) => {
      setState(prev => ({
        ...prev,
        cachedMatchingFormulas: formulas
      }));
    }, []);
    
    const clearCachedMatchingFormulas = useCallback(() => {
      setState(prev => ({
        ...prev,
        cachedMatchingFormulas: null
      }));
    }, []);


  // Step 3: Configuration
  const setThreshold = useCallback((threshold: number) => {
    setState(prev => ({
      ...prev,
      threshold: Math.max(0, Math.min(100, threshold))
    }));
  }, []);

  const setMaxMatches = useCallback((maxMatches: number | null) => {
    setState(prev => ({
      ...prev,
      maxMatches: maxMatches === null ? null : Math.max(1, maxMatches)
    }));
  }, []);

  const setHandleMissingValues = useCallback((option: string) => {
    setState(prev => ({
      ...prev,
      handleMissingValues: option
    }));
  }, []);

  // Navigation
  const nextStep = useCallback(() => {
    setState(prev => ({
      ...prev,
      currentStep: Math.min(3, prev.currentStep + 1)
    }));
  }, []);

  const previousStep = useCallback(() => {
    setState(prev => ({
      ...prev,
      currentStep: Math.max(1, prev.currentStep - 1)
    }));
  }, []);

  const goToStep = useCallback((step: number) => {
    setState(prev => ({
      ...prev,
      currentStep: Math.max(1, Math.min(3, step))
    }));
  }, []);

  // Loading and error states
  const setLoading = useCallback((isLoading: boolean) => {
    setState(prev => ({
      ...prev,
      isLoading
    }));
  }, []);

  const setError = useCallback((error: string | null) => {
    setState(prev => ({
      ...prev,
      error
    }));
  }, []);

  // Reset entire state
  const reset = useCallback(() => {
    setState(INITIAL_STATE);
  }, []);

  // Validation helpers
  const canProceedToStep2 = useCallback(() => {
    return state.leftDataSource && state.rightDataSource && 
           state.leftTableData && state.rightTableData;
  }, [state.leftDataSource, state.rightDataSource, state.leftTableData, state.rightTableData]);

  const canProceedToStep3 = useCallback(() => {
    return state.columnMappings.length > 0;
  }, [state.columnMappings]);

  const canRunMatch = useCallback(() => {
    return canProceedToStep3() && state.threshold >= 0 && state.threshold <= 100;
  }, [canProceedToStep3, state.threshold]);

  // Get all columns from both tables for output selection
  const getAllColumns = useCallback(() => {
    const columns: Array<{ id: string; name: string; source: 'left' | 'right' | 'system' }> = [];
    
    if (state.leftTableData) {
      state.leftTableData.headers.forEach(header => {
        columns.push({
          id: `left_${header}`,
          name: header,
          source: 'left'
        });
      });
    }
    
    if (state.rightTableData) {
      state.rightTableData.headers.forEach(header => {
        columns.push({
          id: `right_${header}`,
          name: header,
          source: 'right'
        });
      });
    }
    
    // Add system columns (match metadata)
    columns.push(
      { id: 'match_score', name: 'Match Score', source: 'system' },
      { id: 'matched_on_left', name: 'Matched Column (Left)', source: 'system' },
      { id: 'matched_on_right', name: 'Matched Column (Right)', source: 'system' },
      { id: 'left_value', name: 'Left Value', source: 'system' },
      { id: 'right_value', name: 'Right Value', source: 'system' }
    );
    
    return columns;
  }, [state.leftTableData, state.rightTableData]);

  // Grounding Table Management
  const setGroundingInfo = useCallback((groundedTable: string, reasoning: string) => {
    setState(prev => ({
      ...prev,
      groundedTable,
      groundingReasoning: reasoning
    }));
  }, []);

  const setGroundingLoading = useCallback((isLoading: boolean) => {
    setState(prev => ({
      ...prev,
      isGroundingLoading: isLoading
    }));
  }, []);

  const clearGroundingInfo = useCallback(() => {
    setState(prev => ({
      ...prev,
      groundedTable: null,
      groundingReasoning: null,
      isGroundingLoading: false
    }));
  }, []);

  // Matching Intent Management
  const setMatchingIntent = useCallback((intent: string | null) => {
    setState(prev => ({
      ...prev,
      matchingIntent: intent
    }));
  }, []);

  const updatePrevState=(params: SmartMatchState)=>{
    setPrevState(params)
  };

  return {
    state,
    
    // Step 1
    setLeftDataSource,
    setRightDataSource,
    
    // Matching Intent
    setMatchingIntent,
    
    // Grounding Table
    setGroundingInfo,
    setGroundingLoading,
    clearGroundingInfo,
    
    // Step 2
    addColumnMapping,
    removeColumnMapping,
    clearColumnMappings,
    swapColumnMappingsInState,
    setMappingGroups,
    toggleOutputColumn,
    setAllOutputColumns,
    getAllColumns,
    
    // Step 3
    setThreshold,
    setMaxMatches,
    setHandleMissingValues,
    
    // Navigation
    nextStep,
    previousStep,
    goToStep,
    
    // UI State
    setLoading,
    setError,
    reset,
    
    // Validation
    canProceedToStep2,
    canProceedToStep3,
    canRunMatch,

    //Matching formulas
    setCachedMatchingFormulas,
    clearCachedMatchingFormulas,

    prevState,
    updatePrevState
  };
}

export type UseSmartMatchReturn = ReturnType<typeof useSmartMatch>;